'use client'

import { Lato } from 'next/font/google'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { useCallback, useState } from 'react'
import { Modal } from '@/Modal'
import { AddToCartCta } from '../_shared/AddToCartCta'
import { ParallaxProductShot } from '../_shared/ParallaxProductShot'
import type { CustomPageProps } from '../_shared/types'

const lato = Lato({ subsets: ['latin'], weight: ['400', '700', '900'] })

/** Gumlet fallbacks when CMS has fewer than 8 images (matches Catch pattern). */
const HOCUS_GALLERY_FALLBACK: Array<{ src: string; alt: string }> = Array.from(
  { length: 8 },
  (_, i) => ({
    src: 'https://vinc.gumlet.io/gallery/productpages/hocus-pocus/cropped-cover.png',
    alt: `Hocus Pocus — photo ${i + 1}`,
  })
)

const thumbBorder =
  'overflow-hidden rounded border-[4px] border-[#F7C26E] hover:opacity-50 transition-opacity max-md:border'

function mergeGalleryImages(
  images: Array<{ src: string; alt: string }>
): Array<{ src: string; alt: string }> {
  return HOCUS_GALLERY_FALLBACK.map((fallback, i) => images[i] ?? fallback)
}

export function HocusPocusPageClient({
  productId,
  productName,
  options,
  hasDownloads,
  isPreorder,
  preorderReleaseDate,
  descriptionHtml,
  images,
}: CustomPageProps) {
  const all = mergeGalleryImages(images)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)

  const openAt = useCallback((index: number) => {
    setActiveIndex(index)
    setLightboxOpen(true)
  }, [])

  const goPrev = useCallback(() => {
    setActiveIndex(i => (i - 1 + all.length) % all.length)
  }, [all.length])

  const goNext = useCallback(() => {
    setActiveIndex(i => (i + 1) % all.length)
  }, [all.length])

  return (
    <div
      className={`bg-black text-white text-lg ${lato.className}`}
      style={{ fontSize: '1.2em' }}
    >
      <section className="max-w-screen-xl mx-auto px-4 pt-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {all.slice(0, 4).map((image, index) => (
            <button
              key={`row1-${image.src}-${index}`}
              type="button"
              onClick={() => openAt(index)}
              className={thumbBorder}
              aria-label={`Open Hocus Pocus gallery image ${index + 1}`}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full aspect-video object-cover"
              />
            </button>
          ))}
        </div>
      </section>

      <section
        className="py-8"
        style={{
          backgroundImage:
            "linear-gradient(90deg, rgba(0,0,0,1) 0%, rgba(9,9,121,0) 49%, rgba(0,0,0,1) 100%), url('https://vinc.gumlet.io/gallery/productpages/hocus-pocus/background.jpg?opacity=20&bg=black')",
          backgroundSize: 'cover',
          backgroundPosition: 'center right',
        }}
      >
        <div className="max-w-screen-xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-4">
              <ParallaxProductShot
                src="https://vinc.gumlet.io/gallery/productpages/hocus-pocus/cropped-cover.png"
                alt="Hocus Pocus by Vanishing Inc."
                className="w-3/4"
                marginBottom="mb-[400px]"
              />
            </div>
            <div className="md:col-span-8 pb-4">
              {/*
                CMS description often ships inline colors (e.g. dark text) that fight the
                dark page — force light copy while keeping links / headings on-brand.
              */}
              <div
                className={[
                  'prose prose-invert max-w-none prose-p:mb-4',
                  'rounded-lg bg-black/50 px-4 py-5 shadow-sm ring-1 ring-white/10 md:bg-black/30 md:px-5 md:py-6',
                  '[&_p]:!text-gray-100 [&_li]:!text-gray-100 [&_ol]:!text-gray-100 [&_ul]:!text-gray-100',
                  '[&_div]:!text-gray-100',
                  '[&_span]:!text-gray-100',
                  '[&_strong]:!text-white [&_b]:!text-white',
                  '[&_em]:!text-gray-200 [&_i]:!text-gray-200',
                  '[&_h1]:!text-white [&_h2]:!text-[#F7C26E] [&_h3]:!text-[#F7C26E] [&_h4]:!text-[#F7C26E]',
                  '[&_a]:!text-[#D88C38] [&_a]:underline [&_a:hover]:!text-[#f5d49a]',
                  '[&_blockquote]:!border-white/40 [&_blockquote]:!text-gray-200',
                ].join(' ')}
                dangerouslySetInnerHTML={{ __html: descriptionHtml || '' }}
              />
              <div className="mt-8">
                <AddToCartCta
                  productId={productId}
                  productName={productName}
                  options={options}
                  hasDownloads={hasDownloads}
                  isPreorder={isPreorder}
                  preorderReleaseDate={preorderReleaseDate}
                  label="Add to cart"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-screen-xl mx-auto px-4 pt-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {all.slice(4, 8).map((image, index) => (
              <button
                key={`row2-${image.src}-${index + 4}`}
                type="button"
                onClick={() => openAt(index + 4)}
                className={thumbBorder}
                aria-label={`Open Hocus Pocus gallery image ${index + 5}`}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full aspect-video object-cover"
                />
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-screen-xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-12 gap-x-10 md:gap-x-16 md:gap-y-14 max-md:text-[0.8em]">
            <div className="space-y-10 md:space-y-12">
              <blockquote className="border-l-4 border-white pl-2 m-0 not-italic">
                <p className="mb-0">
                  &quot;Beautifully done, I adore it.&quot;
                  <br />
                  <cite className="block mt-1 not-italic text-[#F7C26E] text-[0.9em]">
                    &mdash; Reece Shearsmith (Inside No.9, The League of Gentlemen)
                  </cite>
                </p>
              </blockquote>

              <blockquote className="border-l-4 border-white pl-2 m-0 not-italic">
                <p className="mb-0">
                  &quot;The art work is brilliant… the writing both comedic and informative…
                  Rarely can a comic be so well researched.&quot;
                  <br />
                  <cite className="block mt-1 not-italic text-[#F7C26E] text-[0.9em]">
                    &mdash; Ian Keable (magican and historian)
                  </cite>
                </p>
              </blockquote>
            </div>

            <div className="space-y-10 md:space-y-12">
              <blockquote className="border-l-4 border-white pl-2 m-0 not-italic">
                <p className="mb-0">
                  &quot;Witty, compulsive and formally tricksy history of Parapsychology. I can
                  see into you future, and it involves buying this.&quot;
                  <br />
                  <cite className="block mt-1 not-italic text-[#F7C26E] text-[0.9em]">
                    &mdash; Kieron Gillen (<em>The Wicked + The Divine, Uncanny X-Men</em>)
                  </cite>
                </p>
              </blockquote>

              <blockquote className="border-l-4 border-white pl-2 m-0 not-italic">
                <p className="mb-0">
                  &quot;From the very first page, Hocus Pocus is a truly mind blowing read…
                  fascinating and often hilarious… utterly magical… one of the most inventive
                  and gorgeous comics we have read this year.&quot;
                  <br />
                  <cite className="block mt-1 not-italic text-[#F7C26E] text-[0.9em]">
                    &mdash; Pipedream Comics
                  </cite>
                </p>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-screen-xl mx-auto px-4 py-8 text-[0.9em] border-y-2 border-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-4">
          <div className="flex gap-4 flex-wrap sm:flex-nowrap">
            <img
              src="https://vinc.gumlet.io/gallery/productpages/hocus-pocus/wiseman.png"
              alt="Richard Wiseman"
              width={200}
              height={200}
              className="shrink-0 w-[200px] h-[200px] object-cover"
            />
            <div>
              <strong>Richard Wiseman - Creative Consultant</strong>
              <br />
              Professor Richard Wiseman&apos;s books have sold over 3 million copies around
              the world. His viral videos have had over 500 million views. He&apos;s a Member
              of the Inner Magic Circle, a psychologist, was a Director of the Edinburgh
              Fringe Festival, and is an all round lovely chap.
            </div>
          </div>
          <div className="flex gap-4 flex-wrap sm:flex-nowrap">
            <img
              src="https://vinc.gumlet.io/gallery/productpages/hocus-pocus/worth.png"
              alt="Rik Worth"
              width={200}
              height={200}
              className="shrink-0 w-[200px] h-[200px] object-cover"
            />
            <div>
              <strong>Rik Worth - Writer</strong>
              <br />
              Rik Worth is a comics writer, journalist and author. His work has appeared in{' '}
              <em>The Fortean Time</em>, <em>Huffington Post</em>, <em>The Independent</em> and
              many more. As well as writing comics books about history, he has written a book
              about comics history called{' '}
              <em>The Creators of Batman: Bob, Bill and The Dark Knight</em>.
            </div>
          </div>
          <div className="flex gap-4 flex-wrap sm:flex-nowrap">
            <img
              src="https://vinc.gumlet.io/gallery/productpages/hocus-pocus/collver.png"
              alt="Jordan Collver"
              width={200}
              height={200}
              className="shrink-0 w-[200px] h-[200px] object-cover"
            />
            <div>
              <strong>Jordan Collver - Artist</strong>
              <br />
              Jordan Collver is a freelance illustrator. He specializes in using visual
              storytelling, focussing on science, belief, and nature. His work has been featured
              in The London Natural History Museum, Slate, Skeptical Inquirer, Magic, Physics
              World, The Nib, and many comic anthologies. Despite sounding Canadian, he lives
              in Bristol in the UK.
            </div>
          </div>
          <div className="flex gap-4 flex-wrap sm:flex-nowrap">
            <img
              src="https://vinc.gumlet.io/gallery/productpages/hocus-pocus/watts.png"
              alt="Owen Watts"
              width={200}
              height={200}
              className="shrink-0 w-[200px] h-[200px] object-cover"
            />
            <div>
              <strong>Owen Watts - Colorist</strong>
              <br />
              Owen cofounded Comic Creatives UK and is a freelance colorist and artist who has
              saved Jordan Collver from fear and deadlines on many different occasions. As is
              the law when you live in the West Country in the UK, he enjoys an occasional
              glass of cider
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-screen-xl mx-auto px-4 py-8">
        <AddToCartCta
          productId={productId}
          productName={productName}
          options={options}
          hasDownloads={hasDownloads}
          isPreorder={isPreorder}
          preorderReleaseDate={preorderReleaseDate}
          label="Add to cart"
        />
      </section>

      <Modal
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="5xl"
        showCloseButton={true}
        className="m-4"
        backdropOpacity="dark"
      >
        <div className="relative bg-black p-3 md:p-4">
          <button
            type="button"
            onClick={goPrev}
            className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/90 p-2 shadow"
            aria-label="Previous image"
          >
            <ChevronLeftIcon className="w-5 h-5 text-black" />
          </button>
          <button
            type="button"
            onClick={goNext}
            className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/90 p-2 shadow"
            aria-label="Next image"
          >
            <ChevronRightIcon className="w-5 h-5 text-black" />
          </button>
          <img
            src={all[activeIndex].src}
            alt={all[activeIndex].alt}
            className="w-full max-h-[80vh] border-[10px] border-[#D88C38] object-contain rounded-[10px]"
          />
        </div>
      </Modal>
    </div>
  )
}
